import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Clock, FileCheck, User } from 'lucide-react'

import './WorkflowStepCard.scss'

/**
 * Available roles for workflow steps
 * Uses role slugs from the backend roles table
 */
export const WORKFLOW_ROLES = [
	{ value: 'company-admin', label: 'Company Admin' },
	{ value: 'compliance-manager', label: 'Compliance Manager' },
	{ value: 'project-manager', label: 'Project Manager' },
	{ value: 'quality-manager', label: 'Quality Manager' },
	{ value: 'auditor', label: 'Auditor' },
]

/**
 * WorkflowStepCard Component
 *
 * A draggable card representing a single step in a workflow.
 * Includes role selection, approver dropdown, SLA input, and evidence toggle.
 */
const WorkflowStepCard = ({
	step,
	stepNumber,
	users = [],
	onChange,
	onRemove,
	disabled = false,
	isDragging = false,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: step.id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	}

	// Filter users by selected role
	const filteredUsers = useMemo(() => {
		if (!step.role) return []
		return users.filter((user) => user.role_slug === step.role || user.role === step.role)
	}, [users, step.role])

	// Handle field changes
	const handleChange = (field, value) => {
		onChange(step.id, { ...step, [field]: value })
	}

	// Handle role change - reset approver when role changes
	const handleRoleChange = (e) => {
		const newRole = e.target.value
		onChange(step.id, { ...step, role: newRole, approver_id: null })
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`workflow-step-card ${isDragging ? 'workflow-step-card--dragging' : ''} ${disabled ? 'workflow-step-card--disabled' : ''}`}
		>
			{/* Drag Handle */}
			<div className="workflow-step-card__drag-handle" {...attributes} {...listeners}>
				<GripVertical size={20} />
			</div>

			{/* Step Number */}
			<div className="workflow-step-card__number">
				<span>{stepNumber}</span>
			</div>

			{/* Step Content */}
			<div className="workflow-step-card__content">
				{/* Step Name (Optional) */}
				<div className="workflow-step-card__row">
					<div className="workflow-step-card__field workflow-step-card__field--name">
						<label>Step Name (optional)</label>
						<input
							type="text"
							placeholder="e.g., Initial Review"
							value={step.name || ''}
							onChange={(e) => handleChange('name', e.target.value)}
							disabled={disabled}
						/>
					</div>
				</div>

				<div className="workflow-step-card__row">
					{/* Role Selection */}
					<div className="workflow-step-card__field workflow-step-card__field--role">
						<label>
							<User size={14} />
							Role *
						</label>
						<select
							value={step.role || ''}
							onChange={handleRoleChange}
							disabled={disabled}
							required
						>
							<option value="">Select role...</option>
							{WORKFLOW_ROLES.map((role) => (
								<option key={role.value} value={role.value}>
									{role.label}
								</option>
							))}
						</select>
					</div>

					{/* Approver Selection */}
					<div className="workflow-step-card__field workflow-step-card__field--approver">
						<label>Specific Approver</label>
						<select
							value={step.approver_id || ''}
							onChange={(e) => handleChange('approver_id', e.target.value ? parseInt(e.target.value, 10) : null)}
							disabled={disabled || !step.role}
						>
							<option value="">Any user with role</option>
							{filteredUsers.map((user) => (
								<option key={user.id} value={user.id}>
									{user.first_name} {user.last_name}
								</option>
							))}
						</select>
					</div>

					{/* SLA Hours */}
					<div className="workflow-step-card__field workflow-step-card__field--sla">
						<label>
							<Clock size={14} />
							SLA (hours) *
						</label>
						<input
							type="number"
							min="1"
							max="720"
							value={step.sla_hours || 24}
							onChange={(e) => handleChange('sla_hours', parseInt(e.target.value, 10) || 24)}
							disabled={disabled}
							required
						/>
					</div>

					{/* Evidence Required */}
					<div className="workflow-step-card__field workflow-step-card__field--evidence">
						<label>
							<FileCheck size={14} />
							Evidence
						</label>
						<div className="workflow-step-card__toggle">
							<input
								type="checkbox"
								id={`evidence-${step.id}`}
								checked={step.evidence_required === 1 || step.evidence_required === true}
								onChange={(e) => handleChange('evidence_required', e.target.checked ? 1 : 0)}
								disabled={disabled}
							/>
							<label htmlFor={`evidence-${step.id}`}>
								<span className="toggle-label">Required</span>
							</label>
						</div>
					</div>
				</div>
			</div>

			{/* Remove Button */}
			{!disabled && (
				<button
					type="button"
					className="workflow-step-card__remove"
					onClick={() => onRemove(step.id)}
					title="Remove step"
				>
					<Trash2 size={18} />
				</button>
			)}
		</div>
	)
}

WorkflowStepCard.propTypes = {
	step: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		name: PropTypes.string,
		role: PropTypes.string,
		approver_id: PropTypes.number,
		sla_hours: PropTypes.number,
		evidence_required: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
	}).isRequired,
	stepNumber: PropTypes.number.isRequired,
	users: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			first_name: PropTypes.string,
			last_name: PropTypes.string,
			role: PropTypes.string,
			role_slug: PropTypes.string,
		})
	),
	onChange: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	isDragging: PropTypes.bool,
}

export default WorkflowStepCard
